import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, firstValueFrom, from, shareReplay, switchMap, throwError } from 'rxjs';
import { enviroment } from 'src/environments/environment';

/** Resposta de GET /file/map-password/public-key (chave SPKI em Base64). */
interface MapPasswordPublicKeyResponse {
  public_key: string;
  algorithm: string;
  envelope_version: string;
}

/** Prefixo de versão do envelope — o backend recusa qualquer outro. */
const ENVELOPE_VERSION = 'v1';

/**
 * Cifra a senha do mapa/log antes de ela sair do navegador. O backend abre o envelope e só então
 * repassa a senha em claro à Lambda — assim a senha nunca aparece legível em proxies, gateways
 * ou logs intermediários (o TLS continua sendo a proteção principal do trânsito).
 *
 * Envelope híbrido, porque a senha Vision pode ter até 1024 bytes e RSA-2048 cifra no máximo 190:
 *   1. chave AES-256-GCM efêmera cifra a senha (IV de 12 bytes; o WebCrypto anexa a tag de 16 bytes);
 *   2. a chave AES é embrulhada com RSA-OAEP-SHA256 usando a chave pública servida pelo backend;
 *   3. tudo vira `v1.<chaveEmbrulhada>.<iv>.<cifra>` em Base64 padrão.
 * Espelho do `MapPasswordEnvelope` da API (Api FTCloud).
 */
@Injectable({ providedIn: 'root' })
export class MapPasswordCryptoService {
  private publicKey$: Observable<CryptoKey> | null = null;

  constructor(private http: HttpClient) {}

  async encrypt(password: string): Promise<string> {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) {
      // Só acontece fora de contexto seguro (http em IP de rede). O erro sobe até a tela como
      // falha genérica do mapa — melhor do que mandar a senha em claro.
      throw new Error('WebCrypto indisponível: a senha só pode ser enviada em contexto seguro (HTTPS).');
    }

    const rsaKey = await firstValueFrom(this.publicKey());
    const aesKey = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const cipher = await subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, new TextEncoder().encode(password));
    const wrappedKey = await subtle.wrapKey('raw', aesKey, rsaKey, { name: 'RSA-OAEP' });

    return [ENVELOPE_VERSION, toBase64(wrappedKey), toBase64(iv), toBase64(cipher)].join('.');
  }

  /**
   * Chave pública importada uma única vez por sessão (shareReplay). Se a busca falhar, descarta o
   * cache para que a próxima tentativa refaça o GET em vez de repetir o erro para sempre.
   */
  private publicKey(): Observable<CryptoKey> {
    if (!this.publicKey$) {
      const url = `${enviroment.BASE_URL_API}/file/map-password/public-key`;
      this.publicKey$ = this.http.get<MapPasswordPublicKeyResponse>(url).pipe(
        switchMap((res) =>
          from(
            crypto.subtle.importKey(
              'spki',
              fromBase64(res.public_key),
              { name: 'RSA-OAEP', hash: 'SHA-256' },
              false,
              ['wrapKey'],
            ),
          ),
        ),
        catchError((err) => {
          this.publicKey$ = null;
          return throwError(() => err);
        }),
        shareReplay(1),
      );
    }
    return this.publicKey$;
  }
}

function toBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
